<template>
  <div class="payment-qr-page">
    <div class="page-columns">
      <div class="page-left">
        <!-- ===== TOP BAR: Kiosk Status + Search ===== -->
        <div class="top-bar">
          <!-- Kiosk status -->
          <div class="kiosk-strip">
            <div
              :class="[
                'kiosk-dot',
                kioskReady ? 'dot--online' : 'dot--offline',
              ]"
            />
            <span class="text-sm text-gray-600"
              >จอลูกค้า: {{ kioskReady ? "เชื่อมต่อแล้ว" : "ยังไม่เปิด" }}</span
            >
            <vs-button
              color="primary"
              type="border"
              size="small"
              icon-pack="feather"
              icon="icon-monitor"
              @click="openKioskTab"
            >
              {{ kioskReady ? "เปิดจอลูกค้าใหม่" : "เปิดจอลูกค้า" }}
            </vs-button>
            <vs-button
              v-if="kioskReady && member._id"
              color="success"
              type="border"
              size="small"
              icon-pack="feather"
              icon="icon-refresh-cw"
              @click="syncKiosk"
            >
              ซิงค์จอลูกค้า
            </vs-button>
          </div>

          <!-- Search -->
          <div class="search-strip">
            <div class="step-badge step-badge--active">1</div>
            <div class="search-input-wrap">
              <vs-input
                v-model="searchId"
                placeholder="เลขทะเบียนผู้ใช้น้ำ แล้วกด Enter"
                class="w-full"
                @keydown.enter.native="doSearch"
                :disabled="isLoading"
                size="large"
              />
            </div>
            <vs-button
              color="primary"
              type="filled"
              icon-pack="feather"
              icon="icon-search"
              @click="doSearch"
              :disabled="isLoading || !searchId"
            >
              {{ isLoading ? "กำลังค้นหา..." : "ค้นหา" }}
            </vs-button>
            <vs-button
              v-if="member._id"
              color="warning"
              type="border"
              @click="resetPage"
            >
              ล้างข้อมูล
            </vs-button>
            <p v-if="searchError" class="search-error">
              <feather-icon icon="AlertCircleIcon" svgClasses="w-4 h-4 mr-1" />
              {{ searchError }}
            </p>
          </div>
        </div>

        <!-- ===== MAIN CONTENT ===== -->
        <div v-if="member._id" class="main-layout">
          <!-- LEFT: Member Info + Bill List -->
          <div class="left-panel">
            <!-- Member Info (compact) -->
            <div class="member-compact-grid">
              <div class="info-chip">
                <feather-icon
                  icon="UserIcon"
                  svgClasses="w-4 h-4 text-primary"
                />
                <div class="ml-2">
                  <p class="chip-label">ชื่อ - นามสกุล</p>
                  <p class="chip-value">{{ member.name || "-" }}</p>
                </div>
              </div>
              <div class="info-chip">
                <feather-icon
                  icon="HashIcon"
                  svgClasses="w-4 h-4 text-primary"
                />
                <div class="ml-2">
                  <p class="chip-label">เลขที่ผู้ใช้น้ำ</p>
                  <p class="chip-value">{{ member.member_no || "-" }}</p>
                </div>
              </div>
              <div class="info-chip">
                <feather-icon
                  icon="DiscIcon"
                  svgClasses="w-4 h-4 text-primary"
                />
                <div class="ml-2">
                  <p class="chip-label">หมายเลขมาตร</p>
                  <p class="chip-value">{{ member.meter || "-" }}</p>
                </div>
              </div>
              <div class="info-chip">
                <feather-icon
                  icon="MapPinIcon"
                  svgClasses="w-4 h-4 text-primary"
                />
                <div class="ml-2">
                  <p class="chip-label">ที่อยู่</p>
                  <p class="chip-value">{{ member.address || "-" }}</p>
                </div>
              </div>
              <div class="info-chip">
                <feather-icon
                  icon="GitCommitIcon"
                  svgClasses="w-4 h-4 text-primary"
                />
                <div class="ml-2">
                  <p class="chip-label">ซอย / ถนน</p>
                  <p class="chip-value">{{ member.road || "-" }}</p>
                </div>
              </div>
              <div class="info-chip">
                <feather-icon
                  icon="PhoneIcon"
                  svgClasses="w-4 h-4 text-primary"
                />
                <div class="ml-2">
                  <p class="chip-label">เบอร์โทร</p>
                  <p class="chip-value">{{ member.telephone || "-" }}</p>
                </div>
              </div>
            </div>

            <!-- Bill List -->
            <div class="bill-panel">
              <div class="bill-panel-header">
                <div class="flex items-center" style="gap: 8px">
                  <div class="step-badge step-badge--active">2</div>
                  <feather-icon
                    icon="FileTextIcon"
                    svgClasses="w-4 h-4 text-warning"
                  />
                  <span class="font-semibold text-gray-700 text-sm"
                    >รายการค้างชำระ</span
                  >
                  <vs-chip color="warning" class="ml-1" style="font-size: 12px"
                    >{{ bookLists.length }} รายการ</vs-chip
                  >
                </div>
                <div style="display: flex; gap: 6px">
                  <vs-button
                    v-if="bookLists.length > 0"
                    size="small"
                    type="border"
                    color="primary"
                    @click="selectAll"
                    >เลือกทั้งหมด</vs-button
                  >
                  <vs-button
                    v-if="hasChecked"
                    size="small"
                    type="border"
                    color="dark"
                    @click="clearAll"
                    >ยกเลิก</vs-button
                  >
                </div>
              </div>

              <!-- No bills -->
              <div v-if="bookLists.length === 0" class="no-data-box">
                <feather-icon
                  icon="CheckCircleIcon"
                  svgClasses="w-8 h-8 text-success mb-2"
                />
                <p class="text-gray-600 font-medium text-sm">
                  ไม่มีรายการค้างชำระ
                </p>
              </div>

              <!-- Bill Table -->
              <div v-else class="bill-table-wrapper">
                <table class="bill-table w-full">
                  <thead>
                    <tr>
                      <th style="width: 44px">เลือก</th>
                      <th>เดือน / ปี</th>
                      <th>เลขที่ใบแจ้งหนี้</th>
                      <th class="text-right">ค่าน้ำ</th>
                      <th class="text-right">ค่าบริการ</th>
                      <th class="text-right">ค่าบำบัดฯ</th>
                      <th class="text-right">VAT</th>
                      <th class="text-right">ค่าขยะ</th>
                      <th class="text-right" style="color: #2c5dff">
                        รวม (บาท)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(bill, idx) in bookLists"
                      :key="bill._id"
                      :class="[
                        'bill-row',
                        checkBox[idx] ? 'bill-row--selected' : '',
                      ]"
                      @click="toggleRow(idx)"
                    >
                      <td class="text-center">
                        <vs-checkbox
                          v-model="checkBox[idx]"
                          @change.stop="calculateBA"
                          @click.native.stop
                        />
                      </td>
                      <td>
                        <span class="bill-month-badge">
                          {{ splitDate(bill.CB04, "month") }} /
                          {{ splitDate(bill.CB04, "year") }}
                        </span>
                      </td>
                      <td class="text-gray-700">{{ bill.B22 }}</td>
                      <td class="text-right text-gray-700">
                        {{ fmt(bill.BA11) }}
                      </td>
                      <td class="text-right text-gray-700">
                        {{ fmt(bill.BA12) }}
                      </td>
                      <td class="text-right text-gray-700">
                        {{ fmt(bill.BA14) }}
                      </td>
                      <td class="text-right text-gray-700">
                        {{ fmt(bill.BA13) }}
                      </td>
                      <td class="text-right text-gray-700">
                        {{ fmt(bill.BA15) }}
                      </td>
                      <td class="text-right font-bold text-primary">
                        {{ fmt(sumTable[idx]) }}
                      </td>
                    </tr>
                  </tbody>
                  <tfoot v-if="hasChecked">
                    <tr class="bill-total-row">
                      <td colspan="8" class="text-right pr-4 font-semibold">
                        ยอดที่เลือก ({{ selectedCount }} รายการ)
                      </td>
                      <td class="text-right font-bold" style="color: #2c5dff">
                        {{ fmtTotal(sumCheckbox) }} บาท
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
        <!-- /.main-layout -->
      </div>
      <!-- /.page-left -->

      <!-- RIGHT: QR Panel -->
      <div v-if="member._id" class="right-panel">
        <div class="qr-panel-inner">
          <div class="flex items-center mb-3" style="gap: 8px">
            <div class="step-badge step-badge--active">3</div>
            <span class="font-semibold text-gray-700 text-sm"
              >ช่องทางชำระเงิน</span
            >
          </div>

          <!-- No bills selected yet -->
          <div v-if="!hasChecked || sumCheckbox <= 0" class="qr-waiting">
            <feather-icon
              icon="CreditCardIcon"
              svgClasses="w-10 h-10 text-gray-300 mb-2"
            />
            <p class="text-gray-400 text-sm text-center">
              เลือกรายการค้างชำระ<br />เพื่อเลือกช่องทางชำระเงิน
            </p>
          </div>

          <template v-else>
            <!-- Payment channel selector -->
            <div class="channel-selector mb-4">
              <p class="channel-selector-label">เลือกช่องทางชำระเงิน</p>
              <div class="channel-cards">
                <button
                  :class="[
                    'channel-card',
                    'channel-card--cash',
                    paymentChannel === 'cash' ? 'channel-card--selected' : '',
                  ]"
                  @click="setPaymentChannel('cash')"
                >
                  <p class="channel-card-title">เงินสด</p>
                  <p class="channel-card-sub">Cash</p>
                </button>

                <button
                  :class="[
                    'channel-card',
                    'channel-card--qr',
                    paymentChannel === 'qr' ? 'channel-card--selected' : '',
                  ]"
                  @click="setPaymentChannel('qr')"
                >
                  <p class="channel-card-title">QR PromptPay</p>
                  <p class="channel-card-sub">พร้อมเพย์</p>
                </button>
              </div>
            </div>

            <!-- Amount display -->
            <div class="amount-display mb-3">
              <p class="text-xs" style="color: rgba(255, 255, 255, 0.8)">
                ยอดชำระทั้งหมด
              </p>
              <p class="amount-value">{{ fmtTotal(sumCheckbox) }}</p>
              <p class="text-xs" style="color: rgba(255, 255, 255, 0.8)">บาท</p>
            </div>

            <!-- Bill summary list -->
            <div class="bill-summary-list">
              <template v-for="(bill, idx) in bookLists">
                <div
                  v-if="checkBox[idx]"
                  :key="'s-' + idx"
                  class="bill-summary-item"
                >
                  <div>
                    <p class="text-xs font-medium text-gray-700">
                      {{ splitDate(bill.CB04, "month") }} /
                      {{ splitDate(bill.CB04, "year") }}
                    </p>
                    <p class="text-xs text-gray-400">{{ bill.B22 }}</p>
                  </div>
                  <p class="text-xs font-bold text-primary">
                    {{ fmt(sumTable[idx]) }} บาท
                  </p>
                </div>
              </template>
            </div>

            <div class="divider my-3" />

            <!-- Cash channel -->
            <div v-if="paymentChannel === 'cash'" class="cash-channel-box">
              <feather-icon
                icon="DollarSignIcon"
                svgClasses="w-10 h-10 mb-2"
                style="color: #4caf50"
              />
              <p class="text-gray-700 text-sm font-semibold text-center mb-1">
                ชำระด้วยเงินสด
              </p>
              <p class="text-gray-400 text-xs text-center">
                จอลูกค้าจะแสดงยอดเงินสด
              </p>
              <p
                v-if="kioskReady"
                class="text-success text-xs mt-2 text-center"
              >
                <feather-icon
                  icon="MonitorIcon"
                  svgClasses="w-3 h-3 mr-1"
                />แสดงบนจอลูกค้าอัตโนมัติ
              </p>
              <p v-else class="text-warning text-xs mt-2 text-center">
                กรุณากด "เปิดจอลูกค้า" ด้านบนก่อน
              </p>
            </div>

            <!-- QR channel -->
            <template v-else>
              <!-- No promptpay -->
              <div v-if="!promptpayAccount" class="qr-placeholder">
                <feather-icon
                  icon="AlertCircleIcon"
                  svgClasses="w-8 h-8 text-warning mb-2"
                />
                <p class="text-gray-600 text-xs font-medium mb-1 text-center">
                  ยังไม่ได้ตั้งค่าเบอร์ PromptPay
                </p>
                <p class="text-gray-400 text-xs text-center">
                  กรุณาไปที่ ข้อมูลองค์กร แล้วกรอก เบอร์ PromptPay
                </p>
              </div>

              <!-- QR Code -->
              <div v-else class="qr-generated">
                <div class="qr-image-wrapper">
                  <promptpay-qr
                    :id="promptpayAccount"
                    :amount="sumCheckbox"
                    :size="180"
                  />
                </div>
                <p class="text-xs text-gray-400 mt-2 text-center">
                  สแกนด้วยแอปธนาคาร
                </p>
                <div
                  class="mt-2"
                  style="
                    display: flex;
                    gap: 6px;
                    flex-wrap: wrap;
                    justify-content: center;
                  "
                >
                  <vs-button
                    color="dark"
                    type="border"
                    size="small"
                    icon-pack="feather"
                    icon="icon-download"
                    @click="downloadQR"
                  >
                    บันทึก QR
                  </vs-button>
                </div>
                <p
                  v-if="kioskReady"
                  class="text-success text-xs mt-1 text-center"
                >
                  <feather-icon
                    icon="MonitorIcon"
                    svgClasses="w-3 h-3 mr-1"
                  />แสดงบนจอลูกค้าอัตโนมัติ
                </p>
                <p v-else class="text-warning text-xs mt-1 text-center">
                  กรุณากด "เปิดจอลูกค้า" ด้านบนก่อน
                </p>
              </div>
            </template>
          </template>
        </div>
      </div>
    </div>
    <!-- /.page-columns -->

    <!-- ===== PAYMENT FORM ===== -->
    <div v-if="member._id" class="payment-form-section">
      <div class="vx-row mb-4">
        <div class="vx-col sm:w-1/2 w-full">
          <div class="vx-row">
            <div class="vx-col sm:w-1/3 w-full"><span>ยอดเงินชำระ</span></div>
            <div class="vx-col sm:w-2/3 w-full">
              <div class="vx-row">
                <div class="vx-col sm:w-2/3 w-full">
                  <vs-input
                    class="w-full"
                    :value="fmtTotal(sumCheckbox)"
                    readonly
                  />
                </div>
                <div class="vx-col sm:w-1/3 w-full">
                  <span class="ml-2">บาท</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="vx-col sm:w-1/2 w-full">
          <div class="vx-row">
            <div class="vx-col sm:w-1/3 w-full">
              <span>ชำระ</span><span class="text-red align-text-bottom">*</span>
            </div>
            <div class="vx-col sm:w-2/3 w-full">
              <div class="vx-row">
                <div class="vx-col sm:w-2/3 w-full">
                  <vs-input
                    v-validate="'required'"
                    class="w-full"
                    name="money"
                    v-model="money"
                  />
                </div>
                <div class="vx-col sm:w-1/3 w-full">
                  <span class="ml-2">บาท</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="vx-row mb-4">
        <div class="vx-col sm:w-1/2 w-full">
          <div class="vx-row">
            <div class="vx-col sm:w-1/3 w-full">
              <span>ประเภทการชำระ</span
              ><span class="text-red align-text-bottom">*</span>
            </div>
            <div class="vx-col sm:w-2/3 w-full">
              <v-select
                v-validate="'required'"
                name="paybys"
                @input="setPaybys"
                :options="paybys"
                :clearable="false"
                v-model="selectedPayby"
                class="mb-4 md:mb-0"
              />
              <span class="text-danger text-sm" v-show="errors.has('paybys')"
                >กรุณาเลือกประเภทการชำระ</span
              >
            </div>
          </div>
        </div>
        <div class="vx-col sm:w-1/2 w-full">
          <div class="vx-row">
            <div class="vx-col sm:w-1/3 w-full">
              <span>เลขที่บัญชี/เลขที่เช็ค</span>
              <span class="text-red align-text-bottom" v-if="!disableBank"
                >*</span
              >
            </div>
            <div class="vx-col sm:w-2/3 w-full">
              <vs-input
                class="w-full"
                v-model="bankNumber"
                :disabled="disableBank"
                v-validate="'required'"
                name="bankNumber"
              />
              <span
                class="text-danger text-sm"
                v-show="errors.has('bankNumber')"
                v-if="!disableBank"
                >กรุณากรอกเลขที่บัญชี/เลขที่เช็ค</span
              >
            </div>
          </div>
        </div>
      </div>

      <div class="vx-row mb-4">
        <div class="vx-col sm:w-1/2 w-full">
          <div class="vx-row">
            <div class="vx-col sm:w-1/5 w-full">
              <span>เลขที่ใบเสร็จ</span
              ><span class="text-red align-text-bottom">*</span>
            </div>
            <div class="vx-col sm:w-2/5 w-full">
              <vs-input
                class="w-full"
                v-model="number"
                placeholder="เลขที่ใบเสร็จ (ตัวเลขเท่านั้น)"
              />
            </div>
            <div class="vx-col sm:w-2/5 w-full">
              <vs-button type="filled" color="primary" @click="createNumber()"
                >สร้างเลขที่ใบเสร็จ</vs-button
              >
            </div>
          </div>
        </div>
        <div class="vx-col sm:w-1/2 w-full">
          <div class="vx-row">
            <div class="vx-col sm:w-1/4 w-full"><span>กระดาษ</span></div>
            <div class="vx-col sm:w-1/4 w-full">
              <vs-input-number
                min="-100"
                :step="1"
                label="ซ้าย ขวา:"
                v-model="x"
              />
            </div>
            <div class="vx-col sm:w-1/4 w-full">
              <vs-input-number
                min="-100"
                :step="1"
                label="บน ล่าง:"
                v-model="y"
              />
            </div>
            <div class="vx-col sm:w-1/4 w-full">
              <vs-button color="primary" @click="savePaperSetup()"
                >บันทึก</vs-button
              >
            </div>
          </div>
        </div>
      </div>

      <div class="vx-row mb-4">
        <div class="vx-col w-full">
          <div class="flex flex-wrap" style="gap: 8px; align-items: center">
            <PDFReport
              :paidLists="paidList"
              :imgBase64="imgSignBase64"
              :imgLogoBase64="imgLogoBase64"
              :org="org"
              :member="member"
              :orientation="'landscape'"
              :title="'พิมพ์ใบแทนใบเสร็จ(แนวนอน)'"
              :size="[140, 210]"
            />
            <PDFReport
              :paidLists="paidList"
              :imgBase64="imgSignBase64"
              :imgLogoBase64="imgLogoBase64"
              :org="org"
              :member="member"
              :orientation="'portrait'"
              :title="'พิมพ์ใบแทนใบเสร็จ(แนวตั้ง)'"
              :size="[210, 210]"
            />
            <ReceiveBillReport
              :yearTh="yearTh"
              :paidLists="paidList"
              :imgBase64="imgSignBase64"
              :imgLogoBase64="imgLogoBase64"
              :org="org"
              :member="member"
            />
            <ReceiveBillReport590
              :yearTh="yearTh"
              :paidLists="paidList"
              :imgBase64="imgSignBase64"
              :imgLogoBase64="imgLogoBase64"
              :org="org"
              :member="member"
            />
            <vs-button color="primary" @click="paymentChannel === 'qr' ? showQrConfirm = true : submitPayment()"
              >บันทึกการชำระ</vs-button
            >
          </div>
        </div>
      </div>

      <span v-if="paidList.length > 0" class="m-4">รายการที่ชำระเงิน</span>
      <vs-table
        v-if="paidList.length > 0"
        noDataText="ไม่พบข้อมูล"
        :data="paidList"
        class="table-custom mt-5"
      >
        <template slot="thead">
          <vs-th>ลำดับที่</vs-th>
          <vs-th>รอบบิล</vs-th>
          <vs-th>วันที่ชำระเงิน</vs-th>
          <vs-th>ผู้รับเงิน</vs-th>
          <vs-th>เลขที่ใบเสร็จ</vs-th>
          <vs-th>รวมเงิน</vs-th>
        </template>
        <template slot-scope="{ data }">
          <tbody>
            <vs-tr :data="tr" :key="index" v-for="(tr, index) in data">
              <vs-td
                ><p class="product-name font-medium">{{ index + 1 }}</p></vs-td
              >
              <vs-td
                ><p class="product-name font-medium">{{ tr.CB04 }}</p></vs-td
              >
              <vs-td
                ><p class="product-name font-medium">{{ tr.B24 }}</p></vs-td
              >
              <vs-td
                ><p class="product-name font-medium">{{ tr.B09 }}</p></vs-td
              >
              <vs-td
                ><p class="product-name font-medium">
                  {{ tr.B02 || "-" }}
                </p></vs-td
              >
              <vs-td
                ><p class="product-name font-medium">
                  {{ tr.sum || "-" }}
                </p></vs-td
              >
            </vs-tr>
          </tbody>
        </template>
      </vs-table>
    </div>

    <!-- ===== QR PAYMENT CONFIRM DIALOG ===== -->
    <transition name="fade">
      <div v-if="showQrConfirm" class="qr-confirm-overlay" @click.self="showQrConfirm = false">
        <div class="qr-confirm-dialog">
          <div class="qr-confirm-icon">
            <feather-icon icon="AlertCircleIcon" svgClasses="w-10 h-10" style="color:#f59e0b;" />
          </div>
          <h3 class="qr-confirm-title">ยืนยันการชำระเงิน QR PromptPay</h3>
          <p class="qr-confirm-sub">กรุณาตรวจสอบยอดเงินในบัญชีธนาคารก่อนยืนยัน</p>
          <div class="qr-confirm-amount">
            <span class="qr-confirm-amount-label">ยอดที่ต้องได้รับ</span>
            <span class="qr-confirm-amount-value">{{ fmtTotal(sumCheckbox) }}</span>
            <span class="qr-confirm-amount-unit">บาท</span>
          </div>
          <p class="qr-confirm-hint">
            <feather-icon icon="CheckCircleIcon" svgClasses="w-4 h-4 mr-1" style="color:#10b981;" />
            ยืนยันเมื่อได้รับเงินในบัญชีเรียบร้อยแล้ว
          </p>
          <div class="qr-confirm-actions">
            <vs-button color="dark" type="border" @click="showQrConfirm = false">ยกเลิก</vs-button>
            <vs-button color="success" type="filled" @click="showQrConfirm = false; submitPayment()">
              <feather-icon icon="CheckIcon" svgClasses="w-4 h-4 mr-1" />
              ได้รับเงินแล้ว ยืนยัน
            </vs-button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import moduleConfig from "@/store/config/moduleConfig";
import moment from "moment";
import vSelect from "vue-select";
import moduleMember from "@/store/member/moduleMember";
import moduleBookList from "@/store/booklist/moduleBookList";
import modulePayby from "@/store/payby/modulePayby";
import PromptpayQr from "vue-promptpay-qr";
import PDFReport from "../pdf/PaymentReport";
import ReceiveBillReport from "../pdf/ReceiveBillReport";
import ReceiveBillReport590 from "../pdf/ReceiveBillReport590";
import { formatNumber2Digit } from "../pdf/assets/utils";

const CHANNEL_NAME = "prapa_payment_qr";
const KIOSK_PATH = "/kiosk/payment-display";

export default {
  name: "PaymentQRPage",
  components: {
    PromptpayQr,
    vSelect,
    PDFReport,
    ReceiveBillReport,
    ReceiveBillReport590,
  },
  data() {
    return {
      searchId: "",
      searchError: "",
      isLoading: false,
      kioskReady: false,
      channel: null,
      kioskWindow: null,

      member: {
        _id: "",
        name: "",
        member_no: "",
        meter: "",
        address: "",
        road: "",
        telephone: "",
      },

      bookLists: [],
      checkBox: [],
      sumTable: [],
      sumCheckbox: 0,

      paymentChannel: "qr", // 'qr' | 'cash'
      showQrConfirm: false,

      // payment form
      paidList: [],
      number: 0,
      x: 0,
      y: 0,
      money: "",
      selectedPayby: { label: "", value: "" },
      bankNumber: "",
      disableBank: true,
      yearTh: "",
      imgSignBase64: "",
      imgLogoBase64: "",
      baseUrl: process.env.VUE_APP_BASE_URL,
    };
  },
  computed: {
    hasChecked() {
      return this.checkBox.some((v) => v);
    },
    selectedCount() {
      return this.checkBox.filter((v) => v).length;
    },
    org() {
      return this.$store.state.config.item;
    },
    promptpayAccount() {
      return (this.$store.state.config.item || {}).account_no;
    },
    paybys() {
      return (this.$store.state.payby.items || []).map((item) => ({
        label: item.pb02,
        value: item.pb01,
      }));
    },
  },
  methods: {
    async doSearch() {
      if (!this.searchId.trim()) return;
      this.searchError = "";
      this.isLoading = true;
      this.resetMemberData();

      try {
        const res = await this.$store.dispatch(
          "member/fetchMemberListByFilter",
          { M01: this.searchId.trim() }
        );
        if (res.status === 200 && res.data.length > 0) {
          this.setMember(res.data[0]);
          await this.loadBills(res.data[0].M01);
        } else {
          this.searchError = "ไม่พบข้อมูลผู้ใช้น้ำ กรุณาตรวจสอบเลขทะเบียน";
        }
      } catch (e) {
        this.searchError = "เกิดข้อผิดพลาดในการค้นหา กรุณาลองใหม่อีกครั้ง";
      } finally {
        this.isLoading = false;
      }
    },

    setMember(m) {
      this.member = {
        _id: m._id,
        name: m.M04,
        member_no: m.M01,
        meter: m.M27,
        address: m.M05,
        road: m.M06,
        telephone: m.M07,
      };
      this.loadImages();
    },

    async loadBills(memberNo) {
      try {
        const res = await this.$store.dispatch(
          "bookLists/getBookListsForPayment",
          { B03: memberNo, B10: "0", sortBy: "B01:asc" }
        );
        if (res.status === 200 && res.data.length > 0) {
          this.processBills(res.data);
        } else {
          this.bookLists = [];
          this.syncKiosk(); // show member info even when no bills
        }
      } catch (e) {
        this.bookLists = [];
      }
    },

    processBills(bills) {
      const validBills = [];
      const sums = [];
      const checks = [];

      for (const bill of bills) {
        const { B22, BA11, BA12, BA13, BA14, BA15 } = bill;
        if (B22 === "NaN" || BA11 === "NaN" || BA12 === "NaN" || BA13 === "NaN")
          continue;

        const n11 = parseFloat(BA11) || 0;
        const n12 = parseFloat(BA12) || 0;
        const n13 = parseFloat(BA13) || 0;
        const n14 = parseFloat(BA14) || 0;
        const n15 = parseFloat(BA15) || 0;

        validBills.push(bill);
        sums.push(n11 + n12 + n13 + n14 + n15);
        checks.push(true);
      }

      this.bookLists = validBills;
      this.sumTable = sums;
      this.checkBox = checks;
      this.calculateBA(); // also calls syncKiosk inside
    },

    toggleRow(idx) {
      this.$set(this.checkBox, idx, !this.checkBox[idx]);
      this.calculateBA();
    },

    calculateBA() {
      let total = 0;
      for (let i = 0; i < this.checkBox.length; i++) {
        if (this.checkBox[i]) total += this.sumTable[i] || 0;
      }
      this.sumCheckbox = total;
      this.syncKiosk();
    },

    selectAll() {
      this.checkBox = this.checkBox.map(() => true);
      this.calculateBA();
    },

    clearAll() {
      this.checkBox = this.checkBox.map(() => false);
      this.calculateBA();
    },

    setPaymentChannel(channel) {
      this.paymentChannel = channel;
      this.syncKiosk();
    },

    openKioskTab() {
      const url = window.location.origin + KIOSK_PATH;
      if (this.kioskWindow && !this.kioskWindow.closed) {
        this.kioskWindow.focus();
      } else {
        this.kioskWindow = window.open(url, "prapa_kiosk_display");
      }
    },

    syncKiosk() {
      if (!this.kioskReady || !this.channel) return;
      if (!this.member._id) {
        this.channel.postMessage({ type: "CLEAR" });
        return;
      }

      const allBills = this.bookLists.map((b, i) => ({
        period:
          this.splitDate(b.CB04, "month") +
          "/" +
          this.splitDate(b.CB04, "year"),
        billNo: b.B22,
        total: this.sumTable[i],
      }));

      if (this.hasChecked && this.sumCheckbox > 0) {
        const selectedBills = this.bookLists
          .map((b, i) =>
            this.checkBox[i]
              ? {
                  period:
                    this.splitDate(b.CB04, "month") +
                    "/" +
                    this.splitDate(b.CB04, "year"),
                  billNo: b.B22,
                  total: this.sumTable[i],
                }
              : null
          )
          .filter(Boolean);

        this.channel.postMessage({
          type: "SHOW_QR",
          paymentType: this.paymentChannel,
          memberName: this.member.name,
          memberNo: this.member.member_no,
          meter: this.member.meter,
          address: [this.member.address, this.member.road].filter(Boolean).join(" "),
          amount: this.sumCheckbox,
          bills: selectedBills,
          promptpayAccount: this.promptpayAccount,
        });
      } else {
        this.channel.postMessage({
          type: "SHOW_MEMBER",
          memberName: this.member.name,
          memberNo: this.member.member_no,
          meter: this.member.meter,
          address: [this.member.address, this.member.road]
            .filter(Boolean)
            .join(" "),
          bills: allBills,
        });
      }
    },

    downloadQR() {
      const canvas = this.$el.querySelector(".qr-image-wrapper canvas");
      if (!canvas) return;
      const link = document.createElement("a");
      link.download = `QR_${this.member.member_no}_${this.fmtTotal(
        this.sumCheckbox
      )}THB.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    },

    fmt(value) {
      return parseFloat(value || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    fmtTotal(value) {
      return parseFloat(value || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    splitDate(value, type) {
      if (!value) return "-";
      const parts = value.split("-");
      return type === "month" ? parts[0] : parts[1];
    },

    resetMemberData() {
      this.member = {
        _id: "",
        name: "",
        member_no: "",
        meter: "",
        address: "",
        road: "",
        telephone: "",
      };
      this.bookLists = [];
      this.checkBox = [];
      this.sumTable = [];
      this.sumCheckbox = 0;
      this.paymentChannel = "qr";
    },

    resetPage() {
      this.searchId = "";
      this.searchError = "";
      this.resetMemberData();
      this.paidList = [];
      this.money = "";
      if (this.channel) this.channel.postMessage({ type: "CLEAR" });
    },

    async loadImages() {
      const url = this.$store.state.config.item.report_image_url;
      if (url) {
        this.imgSignBase64 = await this.getDataUri(this.baseUrl + url);
      }
      const logoUrl = this.$store.state.config.item.logo_image_url;
      if (logoUrl) {
        this.imgLogoBase64 = await this.getDataUri(this.baseUrl + logoUrl);
      }
    },

    getDataUri(url) {
      return new Promise((resolve) => {
        const image = new Image();
        image.setAttribute("crossOrigin", "anonymous");
        image.onload = function () {
          const canvas = document.createElement("canvas");
          canvas.width = this.width;
          canvas.height = this.height;
          canvas.getContext("2d").drawImage(this, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        };
        image.onerror = () => resolve("");
        image.src = url;
      });
    },

    setPaybys(item) {
      this.disableBank = !(item.value === "03" || item.value === "04");
    },

    savePaperSetup() {
      localStorage.setItem("paper_x", this.x);
      localStorage.setItem("paper_y", this.y);
    },

    async createNumber() {
      if (this.number === "") {
        this.warningDialog("กรุณากรอกเลขที่ใบเสร็จ");
        return;
      }
      if (this.paidList.length === 0) {
        this.failDialog("ไม่มีรายการที่สามารถสร้างใบเสร็จได้");
        return;
      }
      let count = 0;
      for (let i = 0; i < this.paidList.length; i++) {
        const num = parseInt(this.number) + i;
        this.paidList[i].B02 = num.toString();
        const body = { _id: this.paidList[i]._id, B02: num.toString() };
        count++;
        await this.$store
          .dispatch("bookLists/updateItemNumber", body)
          .catch(() => {
            this.failDialog("สร้างเลขที่ใบเสร็จไม่สำเร็จ");
          });
      }
      if (count === 0) {
        this.failDialog("สร้างเลขที่ใบเสร็จไม่สำเร็จ");
      } else {
        this.number = parseInt(this.paidList[this.paidList.length - 1].B02) + 1;
        localStorage.setItem("receive_no", this.number);
        this.createReceiveSuccess();
      }
    },

    async submitPayment() {
      const book = [];
      for (let i = 0; i < this.checkBox.length; i++) {
        if (this.checkBox[i]) book.push(this.bookLists[i]._id);
      }
      if (book.length === 0) {
        this.warningDialog("กรุณาเลือกบิลอย่างน้อย 1 บิล");
        return;
      }
      const user = JSON.parse(sessionStorage.getItem("vuex"));
      const staffId = user.owner.user.user._id;
      const body = {
        memberId: this.member._id,
        staffId,
        list: book,
        payment_type_id: this.selectedPayby.value,
        payment: this.money,
        payment_number: "",
        paymentType: this.paymentChannel,
      };
      if (
        this.selectedPayby.value === "03" ||
        this.selectedPayby.value === "04"
      ) {
        body.payment_number = this.bankNumber;
      }
      await this.$validator.validateAll().then(() => {
        this.$store
          .dispatch("bookLists/updateItemForPayment", body)
          .then((res) => {
            if (res.data.success === false) {
              this.failDialog("กรุณากำหนดรอบส่งเงินก่อนรับชำระเงิน");
            } else if (res.status === 200) {
              const array = res.data.map((data) => {
                data.B24 = this.formatDate(data.B24);
                data.B17 = this.formatTextDate(data.B17);
                data.B19 = this.formatTextDate(data.B19);
                data.Org01 = this.org.Org01;
                data.Org02 = this.org.Org02;
                data.Org03 = this.org.Org03;
                data.Org04 = this.org.Org04;
                data.Org05 = this.org.Org05;
                data.sum = formatNumber2Digit(
                  parseFloat(data.BA11) +
                    parseFloat(data.BA12) +
                    parseFloat(data.BA13) +
                    parseFloat(data.BA14) +
                    parseFloat(data.BA15)
                );
                return data;
              });
              this.paidList = array;
              localStorage.setItem(
                "bookListsPayment",
                JSON.stringify(res.data)
              );
              const remaining = this.bookLists.filter(
                (_, i) => !this.checkBox[i]
              );
              const remainingSums = this.sumTable.filter(
                (_, i) => !this.checkBox[i]
              );
              this.bookLists = remaining;
              this.sumTable = remainingSums;
              this.checkBox = remaining.map(() => false);
              this.sumCheckbox = 0;
              this.syncKiosk();
              this.successDialog();
            } else {
              this.failDialog("บันทึกการชำระเงินไม่สำเร็จ");
            }
          })
          .catch(() => {
            this.failDialog("บันทึกการชำระเงินไม่สำเร็จ");
          });
      });
    },

    formatDate(text) {
      try {
        const date = moment(text).format("DD/MM/YYYY");
        const parts = date.split("/");
        return parts[0] + "/" + parts[1] + "/" + (parseInt(parts[2]) + 543);
      } catch (e) {
        return text;
      }
    },

    formatTextDate(text) {
      try {
        if (text) {
          const date = text.split("/");
          const year =
            date[2].toString().length === 11
              ? parseInt(date[2]) + 2543
              : parseInt(date[2]) + 543;
          return date[0] + "/" + date[1] + "/" + year;
        }
        return "-";
      } catch (e) {
        return text;
      }
    },

    warningDialog(message) {
      this.$vs.dialog({
        type: "alert",
        color: "warning",
        title: "คำเตือน",
        text: message,
        acceptText: "ปิด",
      });
    },
    failDialog(message) {
      this.$vs.dialog({
        type: "alert",
        color: "danger",
        title: "ผิดพลาด",
        text: message,
        acceptText: "ปิด",
      });
    },
    successDialog() {
      this.$vs.dialog({
        type: "alert",
        color: "success",
        title: "สำเร็จ",
        text: "บันทึกการชำระเงินสำเร็จ",
        acceptText: "ปิด",
      });
    },
    createReceiveSuccess() {
      this.$vs.dialog({
        type: "alert",
        color: "success",
        title: "สำเร็จ",
        text: "สร้างเลขที่ใบเสร็จสำเร็จ",
        acceptText: "ปิด",
      });
    },
  },

  mounted() {
    this.channel = new BroadcastChannel(CHANNEL_NAME);
    this.channel.onmessage = (e) => {
      if (e.data.type === "KIOSK_READY") {
        this.kioskReady = true;
        this.syncKiosk(); // push current state to newly opened kiosk
      }
    };
  },

  beforeDestroy() {
    if (this.channel) this.channel.close();
  },

  async created() {
    if (!moduleMember.isRegistered) {
      this.$store.registerModule("member", moduleMember);
      moduleMember.isRegistered = true;
    }
    if (!moduleBookList.isRegistered) {
      this.$store.registerModule("bookLists", moduleBookList);
      moduleBookList.isRegistered = true;
    }
    if (!modulePayby.isRegistered) {
      this.$store.registerModule("payby", modulePayby);
      modulePayby.isRegistered = true;
    }
    if (!moduleConfig.isRegistered) {
      this.$store.registerModule("config", moduleConfig);
      moduleConfig.isRegistered = true;
    }
    await this.$store.dispatch("config/fetchDataItem");
    await this.$store.dispatch("payby/fetchDataListItems");
    if (this.$store.state.payby.items.length > 0) {
      this.selectedPayby = {
        label: this.$store.state.payby.items[0].pb02,
        value: this.$store.state.payby.items[0].pb01,
      };
    }
    if (localStorage.getItem("receive_no") != null)
      this.number = localStorage.getItem("receive_no");
    if (localStorage.getItem("paper_x") != null)
      this.x = localStorage.getItem("paper_x");
    if (localStorage.getItem("paper_y") != null)
      this.y = localStorage.getItem("paper_y");
    this.yearTh = (new Date().getFullYear() + 543).toString();
  },
};
</script>

<style lang="scss" scoped>
// ---- Page wrapper ----
.payment-qr-page {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 130px);
  gap: 10px;
  padding: 4px;
}

// ---- Top bar: kiosk strip + search strip ----
.top-bar {
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  padding: 10px 16px;
  flex-shrink: 0;
}

.kiosk-strip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
  flex-wrap: wrap;
}

.kiosk-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;

  &.dot--online {
    background: #28c76f;
    box-shadow: 0 0 0 3px rgba(40, 199, 111, 0.25);
    animation: blink 2s infinite;
  }
  &.dot--offline {
    background: #b8b8b8;
  }
}

@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

.search-strip {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.search-input-wrap {
  flex: 1;
  min-width: 100px;
  max-width: 200px;

  :deep(.vs-inputx) {
    font-size: 60px;
    font-weight: 600;
  }
}

.search-error {
  color: #ff4c4c;
  font-size: 12px;
  display: flex;
  align-items: center;
  width: 100%;
  margin: 0;
}

// ---- Step badge ----
.step-badge {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
  flex-shrink: 0;

  &--active {
    background: linear-gradient(135deg, #2c5dff, #6c9bff);
    color: #fff;
    box-shadow: 0 2px 6px rgba(44, 93, 255, 0.35);
  }
}

// ---- Main two-column layout ----
// ---- Two-column page layout ----
.page-columns {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.page-left {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.main-layout {
  flex: 1;
}

// ---- Left panel ----
.left-panel {
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;
}

// ---- Member compact grid ----
.member-compact-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  flex-shrink: 0;
}

.info-chip {
  display: flex;
  align-items: center;
  background: #f8f9ff;
  border: 1px solid #e8edff;
  border-radius: 8px;
  padding: 8px 12px;
}

.chip-label {
  font-size: 10px;
  color: #999;
  margin: 0;
  line-height: 1.2;
}

.chip-value {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin: 0;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
}

// ---- Bill panel ----
.bill-panel {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  overflow: hidden;
}

.bill-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 6px;
}

// ---- No data ----
.no-data-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 20px;
  background: #f9fafb;
}

// ---- Bill Table ----
.bill-table-wrapper {
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
}

.bill-table {
  border-collapse: collapse;
  font-size: 13px;

  thead tr {
    background: linear-gradient(135deg, #8794a2, #6b7a8d);
    position: sticky;
    top: 0;
    z-index: 1;

    th {
      color: #fff;
      font-weight: 600;
      padding: 9px 12px;
      white-space: nowrap;
      text-align: left;
    }
  }

  tbody tr.bill-row {
    cursor: pointer;
    transition: background 0.15s;
    border-bottom: 1px solid #eaecf0;

    td {
      padding: 8px 12px;
      vertical-align: middle;
    }

    &:nth-child(even) {
      background: #f0f4fa !important;
    }

    &:nth-child(odd) {
      background: #ffffff !important;
    }

    &:hover {
      background: #e8eeff !important;
    }

    &--selected {
      background: #dce6ff !important;
      td {
        color: #1a3bcc;
      }
    }
  }

  tfoot tr.bill-total-row td {
    background: #f0f5ff;
    border-top: 2px solid #d0dbff;
    padding: 9px 12px;
    font-weight: 600;
  }
}

.bill-month-badge {
  display: inline-block;
  background: #fff3e0;
  color: #e65100;
  border-radius: 20px;
  padding: 1px 8px;
  font-size: 12px;
  font-weight: 600;
}

// ---- Right panel ----
.right-panel {
  flex: 0 0 290px;
  position: sticky;
  top: 0;
  align-self: flex-start;
}

.qr-panel-inner {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  padding: 16px;
}

.qr-waiting {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 10px;
}

// ---- Amount display ----
.amount-display {
  text-align: center;
  padding: 12px;
  background: linear-gradient(135deg, #2c5dff, #6c9bff);
  border-radius: 10px;
  color: #fff;

  .amount-value {
    font-size: 28px;
    font-weight: 800;
    letter-spacing: -1px;
    color: #fff;
    line-height: 1.1;
    margin: 2px 0;
  }
}

// ---- Bill summary ----
.bill-summary-list {
  max-height: 130px;
  overflow-y: auto;
}

.bill-summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
}

.divider {
  height: 1px;
  background: #e0e0e0;
}

// ---- Payment channel selector ----
.channel-selector {
  background: #f7f8fc;
  border-radius: 12px;
  padding: 14px 12px 12px;
  border: 1px solid #e4e8f0;
}

.channel-selector-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #9ca3af;
  margin-bottom: 10px;
  text-align: center;
}

.channel-cards {
  display: flex;
  gap: 10px;
}

.channel-card {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 16px 8px 14px;
  border-radius: 12px;
  border: 2.5px solid #e0e0e0;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;

  &:hover:not(.channel-card--selected) {
    border-color: #bbb;
    background: #fafafa;
    transform: translateY(-1px);
  }
}

.channel-card-check {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 10px;
}

.channel-card-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.channel-icon--cash {
  background: #e8f5e9;
  color: #2e7d32;
}

.channel-icon--qr {
  background: #e8eeff;
  color: #2c5dff;
}

.channel-card-title {
  font-size: 14px;
  font-weight: 800;
  line-height: 1.2;
  margin: 0;
  color: #374151;
}

.channel-card-sub {
  font-size: 11px;
  color: #9ca3af;
  margin: 0;
}

// Cash selected
.channel-card--cash.channel-card--selected {
  border-color: #2e7d32;
  background: linear-gradient(145deg, #e8f5e9, #f1faf1);
  box-shadow: 0 4px 18px rgba(46, 125, 50, 0.22),
    0 0 0 3px rgba(46, 125, 50, 0.12);
  transform: translateY(-2px);

  .channel-card-title {
    color: #1b5e20;
  }
  .channel-card-sub {
    color: #4caf50;
  }
  .channel-icon--cash {
    background: #2e7d32;
    color: #fff;
    box-shadow: 0 4px 12px rgba(46, 125, 50, 0.35);
  }
  .channel-card-check {
    background: #2e7d32;
  }
}

// QR selected
.channel-card--qr.channel-card--selected {
  border-color: #2c5dff;
  background: linear-gradient(145deg, #eef2ff, #f5f7ff);
  box-shadow: 0 4px 18px rgba(44, 93, 255, 0.22),
    0 0 0 3px rgba(44, 93, 255, 0.12);
  transform: translateY(-2px);

  .channel-card-title {
    color: #1a3bcc;
  }
  .channel-card-sub {
    color: #6c9bff;
  }
  .channel-icon--qr {
    background: #2c5dff;
    color: #fff;
    box-shadow: 0 4px 12px rgba(44, 93, 255, 0.35);
  }
  .channel-card-check {
    background: #2c5dff;
  }
}

// ---- Cash channel display ----
.cash-channel-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 8px;
}

// ---- QR code area ----
.qr-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 10px 0;
}

.qr-generated {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.qr-image-wrapper {
  padding: 10px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border: 2px solid #e8edff;

  canvas {
    display: block;
    border-radius: 4px;
  }
}

// ---- Payment form section ----
.payment-form-section {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  padding: 16px;
  flex-shrink: 0;
}

// ---- QR Confirm Dialog ----
.qr-confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  backdrop-filter: blur(2px);
}

.qr-confirm-dialog {
  background: #fff;
  border-radius: 18px;
  padding: 32px 36px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.qr-confirm-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #fef3c7;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
}

.qr-confirm-title {
  font-size: 18px;
  font-weight: 800;
  color: #111827;
  text-align: center;
  margin: 0;
}

.qr-confirm-sub {
  font-size: 13px;
  color: #6b7280;
  text-align: center;
  margin: 0;
}

.qr-confirm-amount {
  display: flex;
  align-items: baseline;
  gap: 6px;
  background: #f0f9ff;
  border: 2px solid #bae6fd;
  border-radius: 12px;
  padding: 14px 28px;
  margin: 8px 0;
}

.qr-confirm-amount-label {
  font-size: 12px;
  color: #0369a1;
  font-weight: 600;
}

.qr-confirm-amount-value {
  font-size: 36px;
  font-weight: 900;
  color: #0c4a6e;
  letter-spacing: -1px;
  line-height: 1;
}

.qr-confirm-amount-unit {
  font-size: 16px;
  color: #0369a1;
  font-weight: 600;
}

.qr-confirm-hint {
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: center;
  margin: 0;
}

.qr-confirm-actions {
  display: flex;
  gap: 10px;
  margin-top: 8px;
  width: 100%;
  justify-content: center;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
